import React from 'react';
import { StatusIndicator, Link } from '@amzn/awsui-components-react/polaris';
import { STATUS_COLOR_TYPE } from './constants';
import { convertEpochToDate } from '../../../utils';
import { Box, ColumnLayout, Popover, SpaceBetween } from '@amzn/awsui-components-react';

export const getReleaseColumns = (onReleaseSelect) => [
  {
    id: 'releaseVersion',
    header: 'Release Version',
    cell: item => (
      <Link
        href="#"
        onFollow={(e) => {
          e.preventDefault();
          onReleaseSelect(item.releaseVersion);
        }}
      >
        {item.releaseVersion}
      </Link>
    ),
    width: 200
  },
  {
    id: 'status',
    header: 'Status',
    cell: item => (
      <StatusIndicator
        type={STATUS_COLOR_TYPE[item.status] || 'info'}
      >
        {item.status}
      </StatusIndicator>
    ),
    width: 150
  },
  {
    id: 'componentSetVersion',
    header: 'Component Set Version',
    cell: item => {
      const csv = item.componentSetVersion;
      if (!csv) return 'N/A';
      return `${csv.componentSetName} - ${csv.majorVersion}.${csv.minorVersion}`;
    },
    width: 250
  },
  {
    id: 'deploymentId',
    header: 'Deployment ID',
    cell: item => item.deploymentId,
    width: 250
  },
  {
    id: 'releaseStartTime',
    header: 'Release Start Time',
    cell: item => item.releaseStartTime ? convertEpochToDate(item.releaseStartTime) : 'N/A',
    width: 200
  }
];

const NotificationDetailsContent = ({ details }) => (
  <Box padding="m">
    <SpaceBetween size="m">
      <div>
        <Box variant="h4">Deployment Start</Box>
        <StatusIndicator
          type={details.deploymentStartNotificationStatus === 'SUCCESS' ? 'success' : 'error'}
        >
          Status: {details.deploymentStartNotificationStatus || 'N/A'}
        </StatusIndicator>
        <div>
          Timestamp: {details.deploymentStartNotificationSentTimestamp ?
          convertEpochToDate(details.deploymentStartNotificationSentTimestamp) : 'N/A'}
        </div>
      </div>

      <div>
        <Box variant="h4">Deployment Complete</Box>
        <StatusIndicator
          type={details.deploymentCompleteNotificationStatus === 'SUCCESS' ? 'success' : 'error'}
        >
          Status: {details.deploymentCompleteNotificationStatus || 'N/A'}
        </StatusIndicator>
        <div>
          Timestamp: {details.deploymentCompleteNotificationSentTimestamp ?
          convertEpochToDate(details.deploymentCompleteNotificationSentTimestamp) : 'N/A'}
        </div>
      </div>

      {details.preDeploymentSoftwareUpdateNotificationStatus &&
        Object.values(details.preDeploymentSoftwareUpdateNotificationStatus).some(value => value) && (
          <div>
            <Box variant="h4">Pre-deployment Software Update Status</Box>
            {Object.entries(details.preDeploymentSoftwareUpdateNotificationStatus)
              .filter(([_, value]) => value)
              .map(([key, value]) => (
                <StatusIndicator key={key} type={value === 'SUCCESS' ? 'success' : 'error'}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}: {value || 'N/A'}
                </StatusIndicator>
              ))}
          </div>
        )}
    </SpaceBetween>
  </Box>
);

export const getDeploymentColumns = () => [
  {
    id: 'clusterId',
    header: 'Cluster ID',
    cell: item => item.clusterId || 'N/A',
  },
  {
    id: 'status',
    header: 'Status',
    cell: item => (
      <StatusIndicator type={STATUS_COLOR_TYPE[item.status]}>
        {item.status}
      </StatusIndicator>
    )
  },
  {
    id: 'requestSource',
    header: 'Request Source',
    cell: item => item.requestSource || 'N/A'
  },
  {
    id: 'deploymentId',
    header: 'Deployment ID',
    cell: item => item.deploymentId || 'N/A'
  },
  {
    id: 'creationTime',
    header: 'Creation Time',
    cell: item => item.creationTime ? convertEpochToDate(item.creationTime) : 'N/A'
  },
  {
    id: 'lastModificationTime',
    header: 'Last Modified',
    cell: item => item.lastModificationTime ? convertEpochToDate(item.lastModificationTime) : 'N/A'
  },
  {
    id: 'deploymentType',
    header: 'Deployment Type',
    cell: item => item.deploymentType || 'N/A'
  },
  {
    id: 'notificationDetails',
    header: 'Notification Details',
    cell: item => {
      const details = item.notificationDetails;
      if (!details) return 'N/A';

      return (
        <Popover
          dismissButton={false}
          position="top"
          size="large"
          expandToViewport
          triggerType="hover"
          content={<NotificationDetailsContent details={details} />}
        >
          <div style={{ cursor: 'pointer' }}>
            <Link variant="info">
              <SpaceBetween size="xxs">
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <Box color="text-body-secondary" fontSize="body-s" padding={{ bottom: 'xxxs' }}>
                      Start Status
                    </Box>
                    <StatusIndicator
                      type={details.deploymentStartNotificationStatus === 'SUCCESS' ? 'success' :
                        details.deploymentStartNotificationSentTimestamp ? 'error' : 'pending'}
                    >
                      {details.deploymentStartNotificationSentTimestamp ?
                        details.deploymentStartNotificationStatus : 'Pending'}
                    </StatusIndicator>
                  </div>
                  <div>
                    <Box color="text-body-secondary" fontSize="body-s" padding={{ bottom: 'xxxs' }}>
                      Complete Status
                    </Box>
                    <StatusIndicator
                      type={details.deploymentCompleteNotificationStatus === 'SUCCESS' ? 'success' :
                        details.deploymentCompleteNotificationSentTimestamp ? 'error' : 'pending'}
                    >
                      {details.deploymentCompleteNotificationSentTimestamp ?
                        details.deploymentCompleteNotificationStatus : 'Pending'}
                    </StatusIndicator>
                  </div>
                </ColumnLayout>
              </SpaceBetween>
            </Link>
          </div>
        </Popover>
      );
    }
  },
  {
    id: 'stackName',
    header: 'Stack Name',
    cell: item => item.stackName || 'N/A'
  },
  {
    id: 'releaseVersion',
    header: 'Release Version',
    cell: item => item.releaseVersion || 'N/A'
  }
];

export const VISIBLE_CONTENT_OPTIONS = [
  { id: "clusterId", label: "Cluster ID" },
  { id: "status", label: "Status" },
  { id: "requestSource", label: "Request Source" },
  { id: "deploymentId", label: "Deployment ID" },
  { id: "creationTime", label: "Creation Time" },
  { id: "lastModificationTime", label: "Last Modified" },
  { id: "deploymentType", label: "Deployment Type" },
  { id: "notificationDetails", label: "Notification Details" },
  { id: "stackName", label: "Stack Name" },
  { id: "releaseVersion", label: "Release Version" }
];